import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Lock, User as UserIcon, ArrowRight, Check, Compass, Building2 } from "lucide-react";
import { Logo } from "@/components/brand/logo";
import { z } from "zod";
import { AuthLayout } from "@/components/auth/auth-layout";
import { AuthField } from "@/components/auth/auth-field";
import { SocialButtons } from "@/components/auth/social-buttons";
import { LoadingButton } from "@/components/auth/loading-button";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name").max(80),
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email").max(255),
  password: z
    .string()
    .min(8, "Use at least 8 characters")
    .max(72, "Password is too long"),
  role: z.enum(["user", "merchant"], { message: "Pick how you'll use Spayzo" }),
});

type FormValues = z.infer<typeof schema>;
type FormErrors = Partial<Record<keyof FormValues, string>>;

export const Route = createFileRoute("/register")({
  component: RegisterPage,
  head: () => ({
    meta: [
      { title: "Create your account — Spayzo" },
      { name: "description", content: "Join Spayzo to book inspiring spaces by the hour or list your own space with instant payouts." },
      { property: "og:title", content: "Create your account — Spayzo" },
      { property: "og:description", content: "Sign up to book or list hourly spaces on Spayzo." },
      { property: "og:url", content: "https://spayzo.lovable.app/register" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/register" }],
  }),
});

function RegisterPage() {
  const { t } = useTranslation();
  const [values, setValues] = useState<FormValues>({
    name: "",
    email: "",
    password: "",
    role: "user",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const update = <K extends keyof FormValues>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((v) => ({ ...v, [k]: e.target.value as FormValues[K] }));
      if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(values);
    if (!result.success) {
      const fe: FormErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FormValues;
        if (!fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 900));
    setSubmitting(false);
  };

  // Live password strength indicator (visual only)
  const pw = values.password;
  const strength =
    (pw.length >= 8 ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);

  return (
    <AuthLayout side="right" hideFooter>
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="mb-8">
          <Logo to="/" className="mb-4 h-8 w-auto text-foreground" />
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("auth.createAccountTitle")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/login" className="font-medium text-primary hover:underline">
              {t("auth.signInLink")}
            </Link>
          </p>
        </div>

        <SocialButtons />

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.or")}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <AuthField
            label={t("common.fullName")}
            icon={UserIcon}
            placeholder={t("auth.namePlaceholder")}
            autoComplete="name"
            value={values.name}
            onChange={update("name")}
            error={errors.name}
          />
          <AuthField
            label={t("common.email")}
            type="email"
            icon={Mail}
            placeholder={t("auth.emailPlaceholder")}
            autoComplete="email"
            value={values.email}
            onChange={update("email")}
            error={errors.email}
          />
          <div>
            <AuthField
              label={t("common.password")}
              type="password"
              icon={Lock}
              placeholder={t("auth.newPasswordPlaceholder")}
              autoComplete="new-password"
              value={values.password}
              onChange={update("password")}
              error={errors.password}
            />
            {pw.length > 0 && !errors.password && (
              <div className="mt-2 flex items-center gap-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      i < strength
                        ? strength <= 2
                          ? "bg-destructive/70"
                          : strength === 3
                            ? "bg-[oklch(0.78_0.15_85)]"
                            : "bg-[oklch(0.65_0.18_145)]"
                        : "bg-muted"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>


          <LoadingButton
            type="submit"
            loading={submitting}
            className="mt-3 h-12 w-full rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-95 active:scale-[0.99] disabled:opacity-70"
          >
            {t("auth.createAccountBtn")} <ArrowRight className="h-4 w-4" />
          </LoadingButton>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("auth.agreePrefix")}{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">{t("footer.terms")}</Link> {t("auth.and")}{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">{t("footer.privacy")}</Link>.
        </p>
      </div>
    </AuthLayout>
  );
}

function RoleOption({
  selected,
  onClick,
  icon: Icon,
  title,
  desc,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`group relative flex flex-col items-start gap-2 rounded-xl border p-3.5 text-start transition-all ${
        selected
          ? "border-primary bg-primary/5 shadow-soft"
          : "border-border bg-card hover:border-foreground/30"
      }`}
    >
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
          selected ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-sm font-semibold leading-tight">{title}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
      <div
        className={`absolute end-3 top-3 flex h-5 w-5 items-center justify-center rounded-full border transition-all ${
          selected ? "border-primary bg-primary text-primary-foreground" : "border-border"
        }`}
      >
        {selected && <Check className="h-3 w-3" strokeWidth={3} />}
      </div>
    </button>
  );
}
